import { Button, ColorInput, Group, Stack, Switch, Text } from "@mantine/core";
import type { AssistantOrbText } from "@/locales/assistant-orb";
import {
	type AssistantOrbConfig,
	hasFixedPalette,
	isOrbColor,
	ORB_COLOR_PRESETS,
} from "@/types/ai-assistant-orb";

/**
 * Pemilih gradien dua stop + preset.
 *
 * Dua stop, bukan tiga: gradien diinterpolasi di ruang OKLCH, sehingga jalur
 * antara dua warna berjauhan sudah melewati warna antara dengan kecerahan yang
 * terjaga — stop ketiga tidak menambah apa pun selain kerumitan untuk admin.
 *
 * Nilai yang belum berupa hex 6 digit tidak diteruskan: ColorInput sempat
 * melaporkan nilai setengah jadi saat admin mengetik.
 */
export function OrbColorPicker({
	config,
	text,
	disabled,
	onChange,
}: {
	config: AssistantOrbConfig;
	text: AssistantOrbText;
	disabled: boolean;
	onChange(patch: Partial<AssistantOrbConfig>): void;
}) {
	if (hasFixedPalette(config.variant)) {
		// Palet tetap: tidak ada kontrol warna yang tampak aktif tapi tak berpengaruh.
		return (
			<Text size="sm" c="dimmed" data-orb-colors data-orb-colors-fixed>
				{text.fixedPalette}
			</Text>
		);
	}
	const colorsLocked = disabled || config.followTheme;
	const setColor = (key: "colorFrom" | "colorTo") => (value: string) => {
		if (isOrbColor(value)) onChange({ [key]: value.toUpperCase() });
	};

	return (
		<Stack gap="sm" data-orb-colors>
			<Group grow align="flex-start">
				<ColorInput
					label={text.colorFrom}
					format="hex"
					value={config.colorFrom}
					disabled={colorsLocked}
					onChange={setColor("colorFrom")}
					data-orb-color-from
				/>
				<ColorInput
					label={text.colorTo}
					format="hex"
					value={config.colorTo}
					disabled={colorsLocked}
					onChange={setColor("colorTo")}
					data-orb-color-to
				/>
			</Group>

			<div>
				<Text size="xs" c="dimmed" mb={6}>
					{text.presets}
				</Text>
				<Group gap={6}>
					{ORB_COLOR_PRESETS.map((preset) => (
						<Button
							key={preset.id}
							size="compact-xs"
							variant="default"
							radius="xl"
							disabled={colorsLocked}
							data-orb-preset={preset.id}
							onClick={() =>
								onChange({ colorFrom: preset.from, colorTo: preset.to })
							}
							leftSection={
								<span
									aria-hidden
									style={{
										width: 10,
										height: 10,
										borderRadius: "50%",
										display: "inline-block",
										background: `linear-gradient(135deg, ${preset.from}, ${preset.to})`,
									}}
								/>
							}
						>
							{text.presetNames[preset.id] ?? preset.id}
						</Button>
					))}
				</Group>
			</div>

			<Switch
				label={text.followTheme}
				description={text.followThemeHint}
				checked={config.followTheme}
				disabled={disabled}
				data-orb-follow-theme
				onChange={(event) =>
					onChange({ followTheme: event.currentTarget.checked })
				}
			/>
		</Stack>
	);
}
